import { Copy, Facebook, LinkedIn, Twitter, WhatsApp } from '@/icons';

import { DEFAULT_TOAST_OPTIONS_INFO } from '@/components/providers';
import { useToastDispatch } from '@/lib/hooks';

import type { IconType } from '@/icons';
import type { FunctionComponent, JSXElementConstructor } from 'react';
import type { TypeShareProps } from './types';

const ShareIcon = ({
  href,
  Icon,
}: {
  href: string;
  Icon: JSXElementConstructor<IconType>;
}) => (
  <a
    href={href}
    className="px-3 py-1.5 transition-colors hover:text-typo-tertiary"
    rel="noopener noreferrer"
    target="_blank"
  >
    <Icon width={28} />
  </a>
);

const SOCIAL_NETWORKS = [
  {
    href: ({ url, title }: { url: string; title: string }) =>
      `https://api.whatsapp.com/send?text=${title}%20${url}`,
    Icon: WhatsApp,
    name: 'whatsapp',
  },
  {
    href: ({ url }: { url: string }) =>
      `https://www.facebook.com/sharer.php?u=${url}`,
    Icon: Facebook,
    name: 'facebook',
  },
  {
    href: ({ url, title }: { url: string; title: string }) =>
      `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
    Icon: Twitter,
    name: 'x',
  },
  {
    href: ({ url }: { url: string }) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    Icon: LinkedIn,
    name: 'linkedin',
  },
];

const copy = (str: string) => navigator?.clipboard?.writeText(str);

const CopyWithToast = ({ url }: Omit<TypeShareProps, 'className'>) => {
  const { toast } = useToastDispatch();

  const copyWithToast = async () => {
    try {
      await copy(url);

      toast('Enlace copiado', {
        ...DEFAULT_TOAST_OPTIONS_INFO,
        link: undefined,
      });
    } catch {}
  };

  return (
    <button
      onClick={copyWithToast}
      className="px-3 py-1.5 transition-colors hover:text-typo-tertiary"
    >
      <Copy width={28} />
    </button>
  );
};

/**
 * Share
 */
export const Share: FunctionComponent<TypeShareProps> = ({
  className = '',
  title,
  url,
  category,
}: TypeShareProps) => (
  <div className={`inline-flex flex-row gap-1 ${className}`}>
    {SOCIAL_NETWORKS.map(({ href, Icon }, index) => (
      <ShareIcon
        key={index}
        href={href({
          url,
          title,
        })}
        Icon={Icon}
      />
    ))}

    <CopyWithToast url={url} title={title} category={category} />
  </div>
);

Share.displayName = 'Share';
